import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Accordion from './Accordion';
import { FaTimes } from 'react-icons/fa';

interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const Drawer: React.FC<DrawerProps> = ({ isOpen, onClose }) => {
  const pathname = usePathname();

  const aboutUsItems = [
    { href: '/our-office', label: 'Our Office' },
    { href: '/our-team', label: 'Our Team' },
    { href: '/testimonials', label: 'Testimonials' },
    { href: '/our-videos', label: 'Our Videos' },
    { href: '/our-community', label: 'Our Community' },
    { href: '/local-communities', label: 'Local Communities' },
    { href: '/faq', label: 'FAQ' },
  ];

  const patientInfoItems = [
    { href: '/new-patients', label: 'New Patients' },
    { href: '/financing-insurance', label: 'Financing & Insurance' },
    { href: '/library', label: 'Dental Health' },
  ];

  const services = {
    General: [
      { display: "Dental Cleanings", route: "dental-cleanings" },
      { display: "Dental Sealants", route: "dental-sealants" },
      { display: "Emergency Treatment", route: "emergency-treatment" },
      { display: "Extractions", route: "extractions" },
      { display: "Fluoride Treatment", route: "fluoride-treatment" },
      { display: "Night Guards", route: "night-guards" },
      { display: "Oral Cancer Screening", route: "oral-cancer-screening" },
      { display: "Oral Exams", route: "oral-exams" },
      { display: "Pediatric Dentistry", route: "pediatric-dentistry" },
      { display: "Periodontal Treatment", route: "periodontal-treatment" },
      { display: "Preventative Program", route: "preventative-program" },
      { display: "Root Canals", route: "root-canals" },
      { display: "Sedation Dentistry", route: "sedation-dentistry" },
      { display: "Sleep Apnea", route: "sleep-apnea" },
      { display: "Snoring Appliances", route: "snoring-appliances" },
      { display: "Sports Mouthguards", route: "sports-mouthguards" },
      { display: "TMJ Disorders", route: "tmj-disorders" },
    ],
    Restorative: [
      { display: "All-on-4®", route: "all-on-4" },
      { display: "All-on-6®", route: "all-on-6" },
      { display: "CEREC", route: "cerec-single-visit-crowns" },
      { display: "Crowns & Caps", route: "crowns-and-caps" },
      { display: "Dental Bridges", route: "dental-bridges" },
      { display: "Dental Fillings", route: "dental-fillings" },
      { display: "Dentures", route: "dentures" },
      { display: "Full Mouth Reconstruction", route: "full-mouth-reconstruction" },
      { display: "Implant Dentistry", route: "implant-dentistry" },
      { display: "Implant Restorations", route: "implant-restorations" },
      { display: "Inlays & Onlays", route: "inlays-onlays" },
    ],
    Cosmetic: [
      { display: "Botox®", route: "botox" },
      { display: "Ceramic Crowns", route: "ceramic-crowns" },
      { display: "Cosmetic Dentistry", route: "cosmetic-dentistry" },
      { display: "Dermal Fillers", route: "dermal-fillers" },
      { display: "Juvederm®", route: "juvederm" },
      { display: "Opalescence® Whitening", route: "opalescence" },
      { display: "Teeth Whitening", route: "teeth-whitening" },
      { display: "Veneers", route: "veneers" },
    ],
    Technology: [
      { display: "Air Abrasion", route: "air-abrasion" },
      { display: "CBCT", route: "cbct" },
      { display: "Dental Lab", route: "dental-lab" },
      { display: "Digital Impressions", route: "digital-impressions" },
      { display: "Digital Radiography", route: "digital-radiography" },
      { display: "E4D", route: "e4d" },
      { display: "LANAP®", route: "lanap" },
      { display: "Laser Dentistry", route: "laser-dentistry" },
      { display: "VELscope® Cancer Screening", route: "velscope-cancer-screening" },
    ],
    Orthodontics: [
      { display: "Invisalign®", route: "invisalign" },
    ],
  };

  const servicePaths = Object.values(services)
    .flat()
    .map((service) => `/services/${service.route}`);

  const isAboutUsActive = aboutUsItems.some((item) => pathname === item.href);
  const isPatientInfoActive = patientInfoItems.some((item) => pathname === item.href);
  const isServicesActive = servicePaths.includes(pathname);

  return (
    <div
      className={`fixed inset-0 bg-black/50 z-[100] transition-opacity duration-300 ${
        isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
      }`}
      onClick={onClose}
    >
      <div
        className={`fixed top-0 right-0 h-full w-80 bg-white shadow-lg transform transition-transform duration-300 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex flex-col p-4 h-full overflow-y-auto">
          <button
            className="self-end text-2xl text-gray-900 mb-4"
            onClick={onClose}
            aria-label="Close menu"
          >
            <FaTimes />
          </button>
          <div className="flex flex-col space-y-2">
            <Link
              href="/"
              className={`px-4 py-2 text-lg font-medium ${
                pathname === '/'
                  ? 'text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]'
                  : 'text-gray-900'
              } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
              onClick={onClose}
            >
              Home
            </Link>
            <Link
              href="/meet-your-dentist"
              className={`px-4 py-2 text-lg font-medium ${
                pathname === '/meet-your-dentist'
                  ? 'text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]'
                  : 'text-gray-900'
              } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
              onClick={onClose}
            >
              Meet Dr. Khaimov
            </Link>
            <Accordion title="About Us" isActive={isAboutUsActive}>
              <ul className="space-y-1">
                {aboutUsItems.map(({ href, label }) => (
                  <li key={href}>
                    <Link
                      href={href}
                      className={`block px-4 py-2 text-base ${
                        pathname === href
                          ? 'text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]'
                          : 'text-gray-600'
                      } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                      onClick={onClose}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Accordion>
            <Link
              href="/blog"
              className={`px-4 py-2 text-lg font-medium ${
                pathname === '/blog'
                  ? 'text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]'
                  : 'text-gray-900'
              } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
              onClick={onClose}
            >
              Blog
            </Link>
            <Accordion title="Services" isActive={isServicesActive}>
              {Object.entries(services).map(([category, items]) => (
                <Accordion key={category} title={category} isSubmenu>
                  <ul className="space-y-1">
                    {items.map((service) => {
                      const servicePath = `/services/${service.route}`;
                      const isActive = pathname === servicePath;
                      return (
                        <li key={service.route}>
                          <Link
                            href={servicePath}
                            className={`block px-4 py-2 text-base ${
                              isActive
                                ? 'text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]'
                                : 'text-gray-600'
                              } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                            onClick={onClose}
                          >
                            {service.display}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </Accordion>
              ))}
            </Accordion>
            <Accordion title="Patient Info" isActive={isPatientInfoActive}>
              <ul className="space-y-1">
                {patientInfoItems.map(({ href, label }) => (
                  <li key={href}>
                    <Link
                      href={href}
                      className={`block px-4 py-2 text-base ${
                        pathname === href
                          ? 'text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]'
                          : 'text-gray-600'
                      } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
                      onClick={onClose}
                    >
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </Accordion>
            <Link
              href="/contact"
              className={`px-4 py-2 text-lg font-medium ${
                pathname === '/contact'
                  ? 'text-white bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text [-webkit-text-fill-color:transparent]'
                  : 'text-gray-900'
              } hover:text-white hover:bg-gradient-to-r hover:from-[#AE7D2F] hover:via-[#ECD05B] hover:to-[#AE7D2F] hover:bg-clip-text hover:[-webkit-text-fill-color:transparent]`}
              onClick={onClose}
            >
              Contact
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Drawer;